import type {useAgent, useRenderToolCall} from "@copilotkit/react-core/v2";
import type {KeyboardEvent, ReactNode, RefObject} from "react";

import {ArrowDownToLine, ArrowsRotateRight, Pencil} from "@gravity-ui/icons";
import {
  Button,
  ChatAttachment,
  ChatAttachmentGroup,
  ChatConversation,
  ChatMessage,
  CopyButton,
  Markdown,
  TextArea,
  Tooltip,
  Typography,
} from "@project/design-system-web";
import {aiStore} from "@project/extensions/ai-assistant";
import {useI18n} from "@project/i18n/react";
import {CodeIcon} from "@project/icons-web";
import {useEffect, useRef, useState} from "react";

import {
  findToolMessageForCall,
  getMessageAttachments,
  getMessageContentText,
  getMessageToolCalls,
  getToolCallDisplayStatus,
  getToolCallId,
  getToolCallName,
  hasRenderableTextContent,
  parseToolCallArgs,
} from "../adapters/copilot-message-adapter";
import {normalizeToolCallStatus} from "../adapters/tool-status-adapter";
import {aiAssistantMessages} from "../ai-assistant.messages";

import {ToolCallWrapper} from "./tool-call-wrapper";

type AgentMessage = ReturnType<typeof useAgent>["agent"]["messages"][number];
type RenderToolCall = ReturnType<typeof useRenderToolCall>;

interface AssistantMessageListProps {
  agentIsRunning: boolean;
  allMessages: AgentMessage[];
  editingMessageId?: string;
  messages: AgentMessage[];
  onCancelUserMessageEdit: () => void;
  onRetryAssistantMessage: (message: AgentMessage) => void;
  onStartUserMessageEdit: (message: AgentMessage) => void;
  onSubmitUserMessageEdit: (message: AgentMessage, nextText: string) => Promise<void>;
  renderToolCall: RenderToolCall;
  userMessageNumbers: Map<string, number>;
  userMessageRefs: RefObject<Map<string, HTMLDivElement>>;
}

function MessageActionButton({
  ariaLabel,
  icon,
  onPress,
  tooltip,
}: {
  ariaLabel: string;
  icon: ReactNode;
  onPress?: () => void;
  tooltip: string;
}) {
  return (
    <Tooltip delay={0}>
      <Button
        isIconOnly
        aria-label={ariaLabel}
        className="text-foreground/40 hover:bg-foreground/5 hover:text-foreground/70 flex size-8 shrink-0 items-center justify-center rounded-md transition-colors"
        onPress={onPress ?? (() => {})}
        size="sm"
        variant="ghost"
      >
        {icon}
      </Button>
      <Tooltip.Content placement="bottom">
        <Typography className="text-sm">{tooltip}</Typography>
      </Tooltip.Content>
    </Tooltip>
  );
}

function downloadMessageAttachments(attachments: ReturnType<typeof getMessageAttachments>) {
  attachments.forEach((attachment) => {
    if (!attachment.downloadUrl) return;

    const link = document.createElement("a");

    link.download = attachment.name;
    link.href = attachment.downloadUrl;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
}

function InlineUserMessageEditor({
  agentIsRunning,
  cancelLabel,
  editMessageLabel,
  initialValue,
  onCancel,
  onSubmit,
  sendLabel,
}: {
  agentIsRunning: boolean;
  cancelLabel: string;
  editMessageLabel: string;
  initialValue: string;
  onCancel: () => void;
  onSubmit: (nextText: string) => Promise<void>;
  sendLabel: string;
}) {
  const [value, setValue] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    textAreaRef.current?.focus();
    textAreaRef.current?.setSelectionRange(initialValue.length, initialValue.length);
  }, [initialValue]);

  const handleSubmit = async () => {
    if (agentIsRunning || isSubmitting || !value.trim()) return;

    setIsSubmitting(true);

    try {
      await onSubmit(value);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Escape") return;

    event.preventDefault();
    onCancel();
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl p-3 shadow-sm">
      <TextArea
        ref={textAreaRef}
        aria-label={editMessageLabel}
        className="w-full"
        disabled={agentIsRunning || isSubmitting}
        minLength={1}
        placeholder={editMessageLabel}
        rows={4}
        style={{resize: "vertical"}}
        value={value}
        variant="secondary"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <div className="flex justify-end gap-2">
        <Button
          isDisabled={agentIsRunning || isSubmitting}
          size="sm"
          variant="ghost"
          onPress={onCancel}
        >
          {cancelLabel}
        </Button>
        <Button
          isDisabled={agentIsRunning || isSubmitting || !value.trim()}
          size="sm"
          variant="primary"
          onPress={() => {
            void handleSubmit();
          }}
        >
          {sendLabel}
        </Button>
      </div>
    </div>
  );
}

export function AssistantMessageList({
  agentIsRunning,
  allMessages,
  editingMessageId,
  messages,
  onCancelUserMessageEdit,
  onRetryAssistantMessage,
  onStartUserMessageEdit,
  onSubmitUserMessageEdit,
  renderToolCall,
  userMessageNumbers,
  userMessageRefs,
}: AssistantMessageListProps) {
  const {t} = useI18n();
  const editMessageLabel = t(aiAssistantMessages.editMessage);
  const cancelEditLabel = t(aiAssistantMessages.cancelEdit);
  const sendEditedMessageLabel = t(aiAssistantMessages.sendEditedMessage);
  const copyMessageLabel = t(aiAssistantMessages.copyMessage);
  const downloadAttachmentsLabel = t(aiAssistantMessages.downloadAttachments);
  const copyResponseLabel = t(aiAssistantMessages.copyResponse);
  const tryAgainLabel = t(aiAssistantMessages.tryAgain);

  return (
    <ChatConversation.Content className="mx-auto w-full max-w-178.5 flex-col gap-8 px-4 pt-8 pb-8">
      {messages.map((message, messageIndex) => {
        const messageId = String(message.id);
        const messageContent = hasRenderableTextContent(message)
          ? getMessageContentText(message)
          : "";
        const messageAttachments = getMessageAttachments(message);
        const toolCalls = getMessageToolCalls(message);
        const renderedToolCalls = toolCalls.map((toolCall, index: number) => {
          const toolCallId = getToolCallId(toolCall);
          const toolMessage = findToolMessageForCall(allMessages, toolCallId);
          const status = getToolCallDisplayStatus(toolCall, toolMessage);

          return (
            <div className="mt-2" key={`${toolCall.id || index}-${status}`}>
              {renderToolCall({
                toolCall: toolCall as never,
                toolMessage: toolMessage as never,
              }) ||
                (() => {
                  // Fallback to our global renderer registry for historical tool calls
                  // whose components have been unmounted (so CopilotKit forgot their render function).
                  const toolName = getToolCallName(toolCall);
                  const customRenderer = aiStore.state.renderers[toolName];
                  const resolvedStatus = normalizeToolCallStatus(status);

                  if (customRenderer) {
                    const CustomRenderer = customRenderer;

                    return (
                      <CustomRenderer
                        args={parseToolCallArgs(toolCall)}
                        status={resolvedStatus}
                        result={toolMessage?.content}
                        respond={undefined}
                        toolCallId={toolCallId}
                      />
                    );
                  }

                  return (
                    <ToolCallWrapper
                      icon={<CodeIcon className="size-4" />}
                      name={toolName}
                      status={resolvedStatus}
                    />
                  );
                })()}
            </div>
          );
        });

        if (message.role === "user") {
          const hasMessageContent = Boolean(messageContent.trim());
          const hasAttachments = messageAttachments.length > 0;
          const isEditing = editingMessageId === messageId;
          const userMessageNumber = userMessageNumbers.get(messageId);

          return (
            <div
              key={message.id}
              ref={(node) => {
                if (node) userMessageRefs.current.set(messageId, node);
                else userMessageRefs.current.delete(messageId);
              }}
              id={`assistant-user-message-${messageId}`}
              data-toc-anchor={userMessageNumber ? "true" : undefined}
              style={{scrollMarginTop: "16px"}}
            >
              <ChatMessage.User className="group">
                {hasAttachments && (
                  <ChatAttachmentGroup className="mb-2 justify-end">
                    {messageAttachments.map((attachment) => (
                      <ChatAttachment
                        key={attachment.id}
                        mediaType={attachment.mediaType}
                        mimeType={attachment.mimeType}
                        name={attachment.name}
                        src={attachment.src}
                      />
                    ))}
                  </ChatAttachmentGroup>
                )}
                {isEditing ? (
                  <InlineUserMessageEditor
                    agentIsRunning={agentIsRunning}
                    cancelLabel={cancelEditLabel}
                    editMessageLabel={editMessageLabel}
                    initialValue={messageContent}
                    onCancel={onCancelUserMessageEdit}
                    onSubmit={(nextText) => onSubmitUserMessageEdit(message, nextText)}
                    sendLabel={sendEditedMessageLabel}
                  />
                ) : (
                  hasMessageContent && (
                    <ChatMessage.Bubble>
                      <ChatMessage.Content>
                        <span className="wrap-break-word whitespace-pre-wrap">
                          {messageContent}
                        </span>
                      </ChatMessage.Content>
                    </ChatMessage.Bubble>
                  )
                )}
                {!isEditing && (hasMessageContent || hasAttachments) && (
                  <div className="mt-1 flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    {hasMessageContent && (
                      <>
                        <CopyButton
                          value={messageContent}
                          copyText={copyMessageLabel}
                          tooltipPlacement="bottom"
                        />
                        <MessageActionButton
                          ariaLabel={editMessageLabel}
                          icon={<Pencil className="size-4" />}
                          onPress={() => onStartUserMessageEdit(message)}
                          tooltip={editMessageLabel}
                        />
                      </>
                    )}
                    {hasAttachments && (
                      <MessageActionButton
                        ariaLabel={downloadAttachmentsLabel}
                        icon={<ArrowDownToLine className="size-4" />}
                        onPress={() => downloadMessageAttachments(messageAttachments)}
                        tooltip={downloadAttachmentsLabel}
                      />
                    )}
                  </div>
                )}
              </ChatMessage.User>
            </div>
          );
        }

        const isCurrentStreamingAssistantMessage =
          agentIsRunning && messageIndex === messages.length - 1;

        return (
          <ChatMessage.Assistant key={message.id} className="group">
            <ChatMessage.Body>
              {messageContent && (
                <>
                  <ChatMessage.Content>
                    <Markdown>{messageContent}</Markdown>
                  </ChatMessage.Content>
                  {!isCurrentStreamingAssistantMessage && (
                    <div className="mt-1 flex gap-1">
                      <CopyButton
                        value={messageContent}
                        copyText={copyResponseLabel}
                        tooltipPlacement="bottom"
                      />
                      <MessageActionButton
                        ariaLabel={tryAgainLabel}
                        icon={<ArrowsRotateRight className="size-4" />}
                        onPress={() => onRetryAssistantMessage(message)}
                        tooltip={tryAgainLabel}
                      />
                    </div>
                  )}
                </>
              )}
              {renderedToolCalls}
            </ChatMessage.Body>
          </ChatMessage.Assistant>
        );
      })}
    </ChatConversation.Content>
  );
}
